import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Send,
  Bot,
  User,
  LayoutDashboard,
  Users,
  Map,
  Radio,
  Settings,
  HelpCircle,
  ChevronRight
} from 'lucide-react';

const QUICK_ACTIONS = [
  { label: 'Go to Dashboard', path: '/dashboard', icon: LayoutDashboard },
  { label: 'View Customers', path: '/customers', icon: Users },
  { label: 'Open Map View', path: '/map', icon: Map },
  { label: 'CYTA API Tools', path: '/cyta-api', icon: Radio },
  { label: 'Settings', path: '/settings', icon: Settings },
];

const FAQ_ITEMS = [
  {
    question: 'How do I add a new customer?',
    answer: 'Go to the Customers page and click the "Add Customer" button in the top right corner. Fill in the customer details including name, contact info, address, and fiber connection details.'
  },
  {
    question: 'How do I check fiber availability?',
    answer: 'Navigate to the CYTA API page. Use the "Check Fiber Availability" section to enter an address and verify if fiber service is available in that area.'
  },
  {
    question: 'How do I view customers on the map?',
    answer: 'Click on "Map View" in the sidebar. You\'ll see all customers with GPS coordinates displayed on an interactive map. Click on markers to see customer details.'
  },
  {
    question: 'How do I filter customers by status?',
    answer: 'On the Customers page, use the status dropdown filter at the top. You can filter by Active, Pending, In Process, and other statuses. You can also click on status cards on the Dashboard to quickly filter.'
  },
  {
    question: 'How do I export customer data?',
    answer: 'On the Customers page, look for the Export button. You can export data to CSV or Excel format. Apply filters first if you want to export a specific subset of customers.'
  },
  {
    question: 'How do I check ONT status?',
    answer: 'Go to the CYTA API page and use the "ONT Status" section. Enter the circuit number or fiber code to check the physical and ethernet status of the optical network terminal.'
  },
  {
    question: 'What do the dashboard statistics mean?',
    answer: 'The Dashboard shows: Total Customers (all records), Active (connected customers), Pending (awaiting installation), In Process (currently being installed). The charts show distribution by status and top areas.'
  },
  {
    question: 'How do I update customer status?',
    answer: 'Open the customer details by clicking on a customer row, then click "Edit". Change the status field and save. The status history is automatically tracked.'
  }
];

const WELCOME_MESSAGE = {
  type: 'bot',
  content: 'Hello! I\'m your FiberTrack assistant. I can help you navigate the application and answer questions about its features. What would you like to know?'
};

const Chatbot = () => {
  const navigate = useNavigate();
  const [messages, setMessages] = useState([WELCOME_MESSAGE]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const findAnswer = (question) => {
    const lowerQuestion = question.toLowerCase();

    // Check for navigation requests
    if (lowerQuestion.includes('dashboard')) {
      return { answer: 'Taking you to the Dashboard...', action: () => navigate('/dashboard') };
    }
    if (lowerQuestion.includes('customer') && (lowerQuestion.includes('add') || lowerQuestion.includes('new') || lowerQuestion.includes('create'))) {
      return { answer: 'Taking you to add a new customer...', action: () => navigate('/customers/new') };
    }
    if (lowerQuestion.includes('customer')) {
      return { answer: 'Taking you to the Customers page...', action: () => navigate('/customers') };
    }
    if (lowerQuestion.includes('map')) {
      return { answer: 'Taking you to the Map View...', action: () => navigate('/map') };
    }
    if (lowerQuestion.includes('cyta') || lowerQuestion.includes('api') || lowerQuestion.includes('fiber availability') || lowerQuestion.includes('ont')) {
      return { answer: 'Taking you to the CYTA API page...', action: () => navigate('/cyta-api') };
    }
    if (lowerQuestion.includes('setting')) {
      return { answer: 'Taking you to Settings...', action: () => navigate('/settings') };
    }

    // Check FAQ items
    for (const faq of FAQ_ITEMS) {
      const keywords = faq.question.toLowerCase().split(' ').filter(w => w.length > 3);
      const matchCount = keywords.filter(kw => lowerQuestion.includes(kw)).length;
      if (matchCount >= 2) {
        return { answer: faq.answer };
      }
    }

    // Check for specific keywords
    if (lowerQuestion.includes('export')) {
      return { answer: FAQ_ITEMS.find(f => f.question.includes('export')).answer };
    }
    if (lowerQuestion.includes('filter') || lowerQuestion.includes('status')) {
      return { answer: FAQ_ITEMS.find(f => f.question.includes('filter')).answer };
    }
    if (lowerQuestion.includes('ont') || lowerQuestion.includes('terminal')) {
      return { answer: FAQ_ITEMS.find(f => f.question.includes('ONT')).answer };
    }

    // Default response
    return {
      answer: 'I\'m not sure about that. Here are some things I can help with:\n\n' +
        '- Navigate to different pages (Dashboard, Customers, Map, CYTA API, Settings)\n' +
        '- Explain how to add or edit customers\n' +
        '- Guide you on using CYTA API features\n' +
        '- Help with filtering and exporting data\n\n' +
        'Try asking a specific question or use the quick actions below!'
    };
  };

  const handleSend = async () => {
    if (!input.trim()) return;

    const userMessage = { type: 'user', content: input };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsTyping(true);

    // Simulate typing delay
    await new Promise(resolve => setTimeout(resolve, 500 + Math.random() * 500));

    const { answer, action } = findAnswer(input);
    const botMessage = { type: 'bot', content: answer };
    setMessages(prev => [...prev, botMessage]);
    setIsTyping(false);

    if (action) {
      setTimeout(action, 1000);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleFaqClick = (faq) => {
    setMessages(prev => [
      ...prev,
      { type: 'user', content: faq.question },
      { type: 'bot', content: faq.answer }
    ]);
  };

  const handleQuickAction = (action) => {
    setMessages(prev => [
      ...prev,
      { type: 'user', content: `Go to ${action.label}` },
      { type: 'bot', content: `Taking you to ${action.label}...` }
    ]);
    setTimeout(() => navigate(action.path), 800);
  };

  return (
    <div className="space-y-4 md:space-y-6">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Assistant</h1>
        <p className="text-gray-600 mt-1 text-sm md:text-base">Get help navigating FiberTrack Pro</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6">
        {/* Chat Section */}
        <div className="lg:col-span-2 card flex flex-col h-[600px]">
          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {messages.map((msg, idx) => (
              <div
                key={idx}
                className={`flex items-start gap-3 ${msg.type === 'user' ? 'flex-row-reverse' : ''}`}
              >
                <div className={`p-2 rounded-full flex-shrink-0 ${
                  msg.type === 'bot' ? 'bg-primary-100' : 'bg-gray-100'
                }`}>
                  {msg.type === 'bot' ? (
                    <Bot size={20} className="text-primary-600" />
                  ) : (
                    <User size={20} className="text-gray-600" />
                  )}
                </div>
                <div className={`max-w-[80%] p-3 rounded-lg ${
                  msg.type === 'bot'
                    ? 'bg-gray-100 text-gray-800'
                    : 'bg-primary-500 text-white'
                }`}>
                  <p className="whitespace-pre-wrap text-sm md:text-base">{msg.content}</p>
                </div>
              </div>
            ))}
            {isTyping && (
              <div className="flex items-start gap-3">
                <div className="p-2 rounded-full bg-primary-100">
                  <Bot size={20} className="text-primary-600" />
                </div>
                <div className="bg-gray-100 p-3 rounded-lg">
                  <div className="flex gap-1">
                    <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                    <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                    <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="border-t p-4">
            <div className="flex gap-2">
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Ask me anything about FiberTrack..."
                className="flex-1 px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
              <button
                onClick={handleSend}
                disabled={!input.trim()}
                className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <Send size={20} />
              </button>
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          {/* Quick Actions */}
          <div className="card">
            <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <ChevronRight size={20} className="text-primary-500" />
              Quick Navigation
            </h2>
            <div className="space-y-2">
              {QUICK_ACTIONS.map((action) => {
                const Icon = action.icon;
                return (
                  <button
                    key={action.path}
                    onClick={() => handleQuickAction(action)}
                    className="w-full flex items-center gap-3 px-4 py-3 text-left rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    <Icon size={18} className="text-primary-500" />
                    <span className="text-gray-700">{action.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* FAQ */}
          <div className="card">
            <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <HelpCircle size={20} className="text-primary-500" />
              Common Questions
            </h2>
            <div className="space-y-2 max-h-[300px] overflow-y-auto">
              {FAQ_ITEMS.map((faq, idx) => (
                <button
                  key={idx}
                  onClick={() => handleFaqClick(faq)}
                  className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  {faq.question}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Chatbot;
